import {
    useCentralErrorSetter,
    useGetErrorInfo,
} from '@experiences/error';
import type {
    GovernancePolicy,
    GovernancePolicyEntity,
    GovernancePolicyFormData,
} from '@experiences/interfaces';
import {
    PolicyEnforcement,
    PolicyStatus,
    Scope,
} from '@experiences/interfaces';
import {
    GovernanceEvent,
    portalTelemetry,
} from '@experiences/telemetry';
import { UiCircularProgress } from '@experiences/ui-common';
import { useNavigateWithParams } from '@experiences/util';
import { isString } from 'lodash';
import React, {
    useCallback,
    useEffect,
    useRef,
} from 'react';
import {
    FormProvider,
    useForm,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
    useLocation,
    useParams,
} from 'react-router';
import useSWR from 'swr';

import { notificationType } from '../../../common/constants/Constant';
import { routePaths } from '../../../common/constants/routePaths';
import { useUiSnackBar } from '../../../common/hooks/useUiSnackBar';
import { useBreadcrumbs } from '../../../common/providers/BreadcrumbProvider';
import { useTenantIdContext } from '../../../common/providers/TenantIdProvider';
import {
    createDataAccessPolicy,
    getDataAccessPolicyById,
    updateDataAccessPolicy,
} from '../../../services/governance/GovernancePolicyService';
import { accountGlobalId } from '../../../store/selectors';
import UiPageContainer from '../../common/UiPageContainer/UiPageContainer';
import AdminBreadCrumbs from '../../organizationsettings/AdminBreadCrumbs';
import {
    diffPolicyFields,
    mapPolicyStatusToPolicyMode,
} from '../governanceTelemetryUtils';
import { GovernanceCreateRouter } from './create/GovernanceCreateRouter';

interface GovernanceWizardComponentProps {
    mode: 'create' | 'edit';
    /** Where to navigate after save/cancel. Defaults to the v1 admin governance list when omitted. */
    returnUrl?: string;
    /** Invoked after a successful create/edit save. Kept telemetry-agnostic so callers own the tracking. */
    onSaved?: (mode: 'create' | 'edit') => void;
}

const GovernanceWizardComponent: React.FC<GovernanceWizardComponentProps> = ({
    mode, returnUrl, onSaved,
}) => {
    const navigate = useNavigateWithParams();
    const location = useLocation();
    const organizationId = useSelector(accountGlobalId);
    const { tenantId: scopedTenantId } = useTenantIdContext();
    const { formatMessage: translate } = useIntl();
    const setErrorMessage = useCentralErrorSetter();
    const { getErrorObject } = useGetErrorInfo();
    const { createNotification } = useUiSnackBar();
    const { breadcrumbs } = useBreadcrumbs();

    // Extract policyId from URL params for edit mode
    const { id: policyId } = useParams();

    // Initialize form with default values
    const methods = useForm<GovernancePolicyFormData>({
        mode: 'onSubmit',
        defaultValues: {
            name: '',
            policyType: 'ToolUsePolicy',
            description: '',
            organizationId,
            tenantId: scopedTenantId || '',
            scope: scopedTenantId ? Scope.Tenant : Scope.Organization,
            enforcement: PolicyEnforcement.Allow,
            status: PolicyStatus.Simulated,
            selectors: [],
            actorRule: undefined,
            executableRule: undefined,
            isBuiltIn: false,
            isTemplate: false,
        },
    });

    const { reset } = methods;

    // Snapshot of the policy data at edit-mode load — used to diff against
    // submitted values for the Governance.PolicyEdited telemetry payload.
    const editOriginalRef = useRef<GovernancePolicyFormData | null>(null);

    // Fetch policy data for edit mode
    const {
        data: policyData,
        isLoading: isLoadingPolicy,
    } = useSWR(
        mode === 'edit' && organizationId && policyId
            ? {
                accountId: organizationId ?? '',
                policyId,
            }
            : null,
        getDataAccessPolicyById,
    );

    // Reset form with loaded policy data in edit mode
    useEffect(() => {
        if (mode === 'edit' && policyData) {
            const scope = policyData.tenantId ? Scope.Tenant : Scope.Organization;
            const formData: GovernancePolicyFormData = {
                name: policyData.name,
                policyType: policyData.policyType,
                description: policyData.description,
                organizationId: policyData.organizationId,
                tenantId: policyData.tenantId,
                scope,
                enforcement: policyData.enforcement,
                status: policyData.status,
                selectors: policyData.selectors,
                actorRule: policyData.actorRule,
                executableRule: policyData.executableRule,
                isBuiltIn: policyData.isBuiltIn,
                isTemplate: policyData.isTemplate,
            };
            editOriginalRef.current = formData;
            // Reset form and explicitly clear dirty state
            reset(formData, { keepDirtyValues: false });
        }
    }, [ mode, policyData, reset ]);

    // Get wizard step from location state to restore the previous step
    const wizardStep = (location.state as { wizardStep?: number })?.wizardStep;

    // When creating from a tenant-level view, skip step 0 (scope selection) — tenant is pre-set
    const computedInitialStep = mode === 'edit'
        ? 4
        : scopedTenantId ? 1 : wizardStep;

    // Submit handler - creates or updates based on mode
    const onSubmitHandler = useCallback(
        async (_data: GovernancePolicyFormData) => {
            try {
                if (mode === 'create') {
                    // Create new policy — strip UI-only `scope` field (same as update path)
                    const {
                        scope: _scope,
                        ...policyData
                    } = _data;
                    const policy: GovernancePolicy = {
                        ...policyData,
                        isBuiltIn: false,
                        isTemplate: false,
                    };
                    if (policy.tenantId?.length === 0) {
                        delete policy.tenantId;
                    }
                    await createDataAccessPolicy({
                        accountId: organizationId,
                        policy,
                    });
                    navigate(returnUrl ?? (scopedTenantId
                        ? routePaths.adminTenantGovernance.replace(':tenantId', scopedTenantId)
                        : routePaths.adminGovernance));
                    createNotification(
                        translate({ id: 'CLIENT_GOVERNANCE_CREATE_POLICY_SUCCESS' }),
                        notificationType.SUCCESS,
                    );
                } else {
                    // Update existing policy
                    const {
                        scope: _scope,
                        ...rest
                    } = _data;

                    if (rest.tenantId == null) {
                        delete rest.tenantId;
                    }

                    const payload: GovernancePolicyEntity = {
                        ...rest,
                        isBuiltIn: false,
                        isTemplate: false,
                        id: policyId ?? '',
                    };

                    await updateDataAccessPolicy({
                        accountId: organizationId,
                        policy: payload,
                    });
                    navigate(returnUrl ?? (scopedTenantId
                        ? routePaths.adminTenantGovernance.replace(':tenantId', scopedTenantId)
                        : routePaths.adminGovernance));
                    createNotification(
                        translate({ id: 'CLIENT_GOVERNANCE_UPDATE_POLICY_SUCCESS' }),
                        notificationType.SUCCESS,
                    );
                    portalTelemetry.trackEvent({
                        name: GovernanceEvent.PolicyEdited,
                        properties: {
                            PolicyId: policyId ?? '',
                            UpdatedFields: editOriginalRef.current
                                ? diffPolicyFields(editOriginalRef.current, _data)
                                : '',
                            PolicyMode: mapPolicyStatusToPolicyMode(_data.status),
                        },
                    });
                }
                // Both create and edit success paths converge here; any failure throws to catch.
                onSaved?.(mode);
            } catch (error) {
                const errorObject = await getErrorObject(error);
                const errorData = errorObject.response?.data;
                const errorResponse = isString(errorData) ? errorData : errorObject?.title;
                setErrorMessage(
                    errorResponse ??
                        translate({ id: 'CLIENT_GOVERNANCE_CREATE_POLICY_ERROR' }),
                );
            }
        },
        [
            mode,
            policyId,
            organizationId,
            returnUrl,
            onSaved,
            navigate,
            createNotification,
            translate,
            getErrorObject,
            setErrorMessage,
        ],
    );

    // Show loading spinner while fetching policy data in edit mode
    if (mode === 'edit' && isLoadingPolicy) {
        return <UiCircularProgress />;
    }

    return (
        <UiPageContainer
            breadcrumb={<AdminBreadCrumbs breadCrumbTrail={breadcrumbs} />}
            position="left"
            maxWidth="1920px"
            enableBackground
            tenantShellHidden
            disableGutter={[ 'left', 'right', 'top', 'bottom' ]}
        >
            <FormProvider {...methods}>
                <GovernanceCreateRouter
                    onSubmitHandler={onSubmitHandler}
                    initialStep={computedInitialStep}
                    mode={mode}
                    returnUrl={returnUrl}
                />
            </FormProvider>
        </UiPageContainer>
    );
};

export default GovernanceWizardComponent;
